import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Avatar, Pill, initialsOf } from '../../../components/ui';
import { AuthImage } from '../../../components/media/AuthImage';
import type { DuplicateCandidate, DuplicateEntityType, DuplicateSide, MatchSignal } from '../../../types/duplicates';
import { VERDICT_LABEL, VERDICT_SENTENCE, attachmentSummary, describeSignal, entityHref, shortDate, verdictTone } from '../format';

/** Photo or logo when there is one, initials otherwise */
export const RecordAvatar: React.FC<{
  name: string;
  imageId?: string | null;
  kind: DuplicateEntityType;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}> = ({ name, imageId, kind, size = 'md' }) => (
  <Avatar
    name={name}
    kind={kind === 'organization' ? 'organization' : 'stakeholder'}
    size={size}
    image={
      imageId ? (
        <AuthImage
          mediaId={imageId}
          alt=""
          className="h-full w-full object-cover"
          fallback={initialsOf(name)}
        />
      ) : undefined
    }
  />
);

/** How sure the server is, as a word first and a number second */
export const VerdictMark: React.FC<{ verdict: DuplicateCandidate['verdict']; score: number }> = ({ verdict, score }) => (
  <span className="inline-flex items-center gap-2">
    <Pill tone={verdictTone(verdict)} title={VERDICT_SENTENCE[verdict]}>
      {VERDICT_LABEL[verdict]}
    </Pill>
    <span className="text-[12px] tabular-nums text-neutral-400" title={`Match score ${score} out of 100`}>
      {Math.round(score)}
    </span>
  </span>
);

/**
 * One record: who it is, when it arrived and what hangs off it — the three
 * things that decide whether it is the duplicate or the keeper.
 */
export const RecordSummary: React.FC<{
  side: DuplicateSide;
  entityType: DuplicateEntityType;
  /** Small grey word above the name, e.g. "Keeping" */
  eyebrow?: React.ReactNode;
  linked?: boolean;
  className?: string;
}> = ({ side, entityType, eyebrow, linked = true, className = '' }) => {
  const attached = attachmentSummary(side);
  const name = linked ? (
    <Link
      to={entityHref(entityType, side.id)}
      target="_blank"
      rel="noreferrer"
      className="truncate text-[13px] text-ink hover:underline hover:decoration-neutral-300 hover:underline-offset-2"
    >
      {side.label}
    </Link>
  ) : (
    <span className="truncate text-[13px] text-ink">{side.label}</span>
  );

  return (
    <div className={`min-w-0 ${className}`}>
      {eyebrow && <p className="mb-1.5 text-[12px] text-neutral-400">{eyebrow}</p>}
      <div className="flex min-w-0 items-center gap-2.5">
        <RecordAvatar name={side.label} imageId={side.imageId} kind={entityType} />
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">{name}</div>
          <p className="truncate text-[12px] text-neutral-500">{side.subtitle || 'No role recorded'}</p>
        </div>
      </div>
      <p className="mt-2 text-[12px] text-neutral-400">Added {shortDate(side.createdAt) || 'unknown'}</p>
      <p className="mt-0.5 text-[12px] text-neutral-500">{attached || 'Nothing attached'}</p>
    </div>
  );
};

/** The evidence, in the words of the two records themselves */
export const SignalRow: React.FC<{ signal: MatchSignal }> = ({ signal }) => {
  const { label, shared, left, right } = describeSignal(signal);
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[13px]">
      <span className="text-neutral-500">{label}</span>
      {shared ? (
        <span className="min-w-0 break-words text-ink">{shared}</span>
      ) : (
        <span className="inline-flex min-w-0 flex-wrap items-baseline gap-1.5 text-ink">
          <span className="break-words">{left || '—'}</span>
          <ArrowRight className="h-3 w-3 flex-shrink-0 self-center text-neutral-300" strokeWidth={1.75} />
          <span className="break-words">{right || '—'}</span>
        </span>
      )}
      <span className="ml-auto flex-shrink-0 text-[12px] tabular-nums text-neutral-300" title="Contribution to the match score">
        +{Math.round(signal.weight)}
      </span>
    </li>
  );
};

export const SignalList: React.FC<{ signals: MatchSignal[]; className?: string }> = ({ signals, className = '' }) => (
  <ul className={`space-y-1.5 ${className}`}>
    {signals.map((signal, index) => (
      <SignalRow key={`${signal.reason}-${index}`} signal={signal} />
    ))}
  </ul>
);

/**
 * One honest line about what is wrong and what to do — never a spinner that
 * goes nowhere. `tone="quiet"` for "nothing here", loud for real failures.
 */
export const Notice: React.FC<{
  children: React.ReactNode;
  tone?: 'quiet' | 'caution' | 'error';
  action?: React.ReactNode;
  className?: string;
}> = ({ children, tone = 'error', action, className = '' }) => (
  <div
    role={tone === 'error' ? 'alert' : 'status'}
    className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl px-3.5 py-2.5 text-[13px] ${
      tone === 'error'
        ? 'bg-primary-50 text-primary-700'
        : tone === 'caution'
          ? 'bg-amber-50 text-amber-700'
          : 'bg-neutral-50 text-neutral-600'
    } ${className}`}
  >
    <span className="min-w-0 flex-1">{children}</span>
    {action}
  </div>
);
